/** API de prerender (Chrome); aún no está en lib.dom de TypeScript del proyecto. */
type DocumentWithPrerendering = Document & { readonly prerendering?: boolean };

const MICROSOFT_UA_PATTERN = /Outlook|MSOffice|Microsoft Office|Microsoft Outlook|OutlookWebView|OfficeLiveConnector/i;
const MICROSOFT_REFERRER_PATTERN =
	/safelinks\.protection\.outlook\.com|outlook\.office\.com|outlook\.live\.com|outlook\.office365\.com/i;

/** Reintentos cortos mientras la pestaña es visible y hasFocus llega con retraso (NIF-590). */
const HAS_FOCUS_POLL_ATTEMPTS = 10;
const HAS_FOCUS_POLL_INTERVAL_MS = 100;

let userGestureCapturedAtEntry = false;

/**
 * Captura síncrona del gesto de apertura del enlace (sticky activation).
 * Debe llamarse lo antes posible: prefetch de Gmail no tiene userActivation.
 */
export function captureUserActivationAtEntry(): void {
	if (userGestureCapturedAtEntry || typeof navigator === 'undefined') {
		return;
	}

	const userActivation = navigator.userActivation;
	userGestureCapturedAtEntry = Boolean(userActivation?.isActive || userActivation?.hasBeenActive);
}

/** Solo para tests. */
export function resetUserActivationCaptureForTests(): void {
	userGestureCapturedAtEntry = false;
}

captureUserActivationAtEntry();

function isDocumentPrerendering(doc: Document = document): boolean {
	return Boolean((doc as DocumentWithPrerendering).prerendering);
}

function hasUserGesture(): boolean {
	if (userGestureCapturedAtEntry) {
		return true;
	}

	if (typeof navigator === 'undefined') {
		return false;
	}

	const userActivation = navigator.userActivation;
	return Boolean(userActivation?.isActive || userActivation?.hasBeenActive);
}

function hasUserActivationApi(): boolean {
	return typeof navigator !== 'undefined' && navigator.userActivation != null;
}

/**
 * Firefox implementa userActivation, pero al pegar la URL en la barra de direcciones
 * el documento suele quedar visible sin hasFocus ni sticky activation hasta un clic.
 * El prefetch agresivo de Gmail es propio de Chromium; en Gecko visible basta.
 */
function isFirefoxEngine(): boolean {
	return typeof navigator !== 'undefined' && /Firefox\//.test(navigator.userAgent);
}

/**
 * Safari / WebKit puro (NIF-790): Safari macOS e iOS Safari exponen `userActivation` desde 16.4,
 * pero al abrir el enlace la pestaña suele quedar visible sin hasFocus ni sticky hasta un toque.
 * Excluye Chromium, CriOS, Edge (prefetch Gmail es Chromium de escritorio).
 */
function isWebKitSafariEngine(): boolean {
	if (typeof navigator === 'undefined') {
		return false;
	}
	const ua = navigator.userAgent;
	return /AppleWebKit/i.test(ua) && !/Chrome|Chromium|CriOS|Edg|EdgiOS/i.test(ua);
}

/**
 * iOS completo (NIF-790): CriOS y WebViews llevan Chrome/CriOS en el UA pero siguen siendo WebKit;
 * al abrir el enlace la pestaña suele quedar visible sin hasFocus hasta un toque.
 */
function isIosDevice(): boolean {
	if (typeof navigator === 'undefined') {
		return false;
	}
	const ua = navigator.userAgent;
	if (/iPhone|iPad|iPod/i.test(ua)) {
		return true;
	}
	// iPadOS 13+ puede reportar Macintosh en el UA.
	return /Macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
}

/**
 * Outlook / Safe Links suelen precargar el enlace al abrir el correo con JS en WebView.
 * En ese contexto no basta con visible+foco para considerar visita real.
 */
export function isMicrosoftEmailClientContext(doc: Document = document): boolean {
	if (typeof navigator !== 'undefined' && MICROSOFT_UA_PATTERN.test(navigator.userAgent)) {
		return true;
	}

	const referrer = doc.referrer ?? '';
	return MICROSOFT_REFERRER_PATTERN.test(referrer);
}

/**
 * Señales de visita real (apertura del enlace), sin clic extra en la SPA.
 * Prefetch de correo suele carecer de userActivation y de foco en pestaña.
 */
export function isRealUserVisit(doc: Document = document): boolean {
	if (isDocumentPrerendering(doc)) return false;
	if (hasUserGesture()) return true;
	if (isMicrosoftEmailClientContext(doc)) return false;

	if (doc.visibilityState !== 'visible') return false;

	// Sin userActivation API, Firefox, Safari/WebKit o iOS: hasFocus flaky / foco en chrome.
	// Chromium de escritorio: visible sin foco = prefetch Gmail típico → no registrar.
	if (!hasUserActivationApi() || isFirefoxEngine() || isWebKitSafariEngine() || isIosDevice()) {
		return true;
	}

	return doc.hasFocus();
}

function waitForVisitSignals(): Promise<void> {
	return new Promise((resolve) => {
		let settled = false;
		let focusPollTimerId: ReturnType<typeof setTimeout> | null = null;
		let focusPollAttempts = 0;

		const finish = (): void => {
			if (settled) {
				return;
			}
			settled = true;
			cleanup();
			resolve();
		};

		const tryResolveFromPassiveSignals = (): void => {
			if (isRealUserVisit()) {
				finish();
				return;
			}
			scheduleHasFocusPollIfNeeded();
		};

		const resolveFromUserInteraction = (): void => {
			finish();
		};

		const clearHasFocusPoll = (): void => {
			if (focusPollTimerId !== null) {
				clearTimeout(focusPollTimerId);
				focusPollTimerId = null;
			}
		};

		const scheduleHasFocusPollIfNeeded = (): void => {
			if (settled || isMicrosoftEmailClientContext()) return;
			if (document.visibilityState !== 'visible') {
				clearHasFocusPoll();
				focusPollAttempts = 0;
				return;
			}
			if (focusPollTimerId !== null || focusPollAttempts >= HAS_FOCUS_POLL_ATTEMPTS) {
				return;
			}

			focusPollTimerId = setTimeout(() => {
				focusPollTimerId = null;
				focusPollAttempts += 1;
				if (isRealUserVisit()) {
					finish();
					return;
				}
				scheduleHasFocusPollIfNeeded();
			}, HAS_FOCUS_POLL_INTERVAL_MS);
		};

		const cleanup = (): void => {
			clearHasFocusPoll();
			document.removeEventListener('visibilitychange', tryResolveFromPassiveSignals);
			document.removeEventListener('focusin', tryResolveFromPassiveSignals);
			window.removeEventListener('focus', tryResolveFromPassiveSignals);
			window.removeEventListener('pageshow', tryResolveFromPassiveSignals);
			window.removeEventListener('pointerdown', resolveFromUserInteraction);
			window.removeEventListener('touchstart', resolveFromUserInteraction);
			window.removeEventListener('keydown', resolveFromUserInteraction);
		};

		tryResolveFromPassiveSignals();
		if (settled) {
			return;
		}

		document.addEventListener('visibilitychange', tryResolveFromPassiveSignals);
		document.addEventListener('focusin', tryResolveFromPassiveSignals);
		window.addEventListener('focus', tryResolveFromPassiveSignals);
		window.addEventListener('pageshow', tryResolveFromPassiveSignals);
		window.addEventListener('pointerdown', resolveFromUserInteraction, { passive: true });
		window.addEventListener('touchstart', resolveFromUserInteraction, { passive: true });
		window.addEventListener('keydown', resolveFromUserInteraction);
	});
}

export function waitForRealUserVisit(): Promise<void> {
	captureUserActivationAtEntry();

	if (isRealUserVisit()) {
		return Promise.resolve();
	}

	if (isDocumentPrerendering()) {
		return new Promise((resolve) => {
			document.addEventListener(
				'prerenderingchange',
				() => {
					if (isRealUserVisit()) {
						resolve();
						return;
					}
					void waitForVisitSignals().then(resolve);
				},
				{ once: true },
			);
		});
	}

	return waitForVisitSignals();
}
